import { Link } from '@src/components';
import { Prose } from '@src/components/Prose/Prose';

export function PolicyCom() {
  return (
    <Prose>
      <h1>Privacy Policy</h1>

      <span className="prose__meta">Last updated: October 23, 2026</span>

      <p>
        This Privacy Policy explains how SantaSecret collects, uses, shares, and retains personal
        information when you use <mark>santa-secret.com</mark> and the Secret Santa services
        available through the website.
      </p>

      <h2 id="list-1">1. Who We Are</h2>
      <p>
        SantaSecret is operated by <mark>Santa Secret LLC</mark>, a California limited liability
        company (&quot;SantaSecret&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). Santa
        Secret LLC is the controller of personal information processed through SantaSecret for
        purposes of applicable data protection laws. Our contact details are in section 16.
      </p>

      <h2 id="list-2">2. Information We Collect</h2>

      <h3>2.1 Information you provide directly</h3>
      <p>Depending on how you use SantaSecret, you may provide us with:</p>
      <ul>
        <li>your email address;</li>
        <li>your name or nickname;</li>
        <li>
          a phone number and postal address, if you enter them on your participant card for delivery
          of a physical gift;
        </li>
        <li>
          gift wishes, preferences, and other information you voluntarily enter on a participant
          card;
        </li>
        <li>
          messages you send in the in-game chats &mdash; the anonymous chat with your assigned
          Secret Santa and the chat with the game organizer;
        </li>
        <li>a picture you upload to use as your card avatar;</li>
        <li>messages and information you send to our support team.</li>
      </ul>

      <h3>2.2 Information provided by a game organizer</h3>
      <p>
        If you did not provide your personal information directly to SantaSecret, it may have been
        provided by the organizer of a Secret Santa game. An organizer typically provides your name
        or nickname, and may also provide your email address if they want SantaSecret to send you an
        invitation, draw result, or reminder.
      </p>
      <p>
        By submitting another person&apos;s name, email address, or other personal information, an
        organizer confirms that they have informed that person and have that person&apos;s
        permission, or another lawful authority, to provide the information to SantaSecret for
        invitations, draw notifications, and participation in the relevant Secret Santa game. The
        organizer must be able to demonstrate this permission or authority upon request.
      </p>
      <p>
        If you do not want SantaSecret to retain or process information provided about you by an
        organizer, contact us at{' '}
        <Link variant="inline" href="mailto:postman@santa-secret.com">
          postman@santa-secret.com
        </Link>
        .
      </p>

      <h3>2.3 Information collected automatically</h3>
      <p>
        When you use SantaSecret, our systems automatically process technical information such as:
      </p>
      <ul>
        <li>your IP address;</li>
        <li>your browser and device information;</li>
        <li>timestamps and request information;</li>
        <li>security, technical, and error logs;</li>
        <li>information necessary to maintain your session and operate the service.</li>
      </ul>
      <p>
        We also collect limited information about how you interact with the website (such as pages
        visited and actions taken) through our SantaSecret-hosted analytics, as described in section
        6.
      </p>

      <h2 id="list-3">3. How and Why We Use Information</h2>
      <p>We use personal information only to operate, secure, and improve SantaSecret.</p>

      <h3>3.1 Providing the service</h3>
      <p>We process information to:</p>
      <ul>
        <li>create and manage user accounts;</li>
        <li>create, join, and administer Secret Santa games;</li>
        <li>create and manage participant cards;</li>
        <li>conduct the random Secret Santa drawing;</li>
        <li>provide participants with their assigned recipient;</li>
        <li>let organizers administer games and communicate with participants;</li>
        <li>pass gift preferences and delivery information to the appropriate participants;</li>
        <li>provide other functionality requested by users.</li>
      </ul>
      <p>
        Where the GDPR applies, we rely on the necessity of this processing to provide the service
        you requested or to perform our agreement with you.
      </p>

      <h3>3.2 Organizer-provided participant information</h3>
      <p>
        Where an organizer provides a participant&apos;s name or email address, we process that
        information to conduct the Secret Santa game requested by the organizer, send the relevant
        invitation or draw notification, and make the service function for that group.
      </p>
      <p>
        Where the GDPR applies, we rely on our legitimate interest in providing the requested Secret
        Santa service, subject to the rights and interests of the affected participant.
      </p>

      <h3>3.3 Service communications</h3>
      <p>
        We use contact information to send messages necessary for the operation of SantaSecret,
        including invitations, draw results, reminders, account notifications, security and
        technical messages, and responses to support requests. We do not use participant information
        for unrelated mass marketing.
      </p>

      <h3>3.4 Security and reliability</h3>
      <p>
        We process technical information as reasonably necessary to prevent abuse, diagnose errors,
        protect users, and keep the service operating. Where the GDPR applies, this processing is
        based on our legitimate interest in maintaining a secure and reliable service.
      </p>

      <h3>3.5 Analytics</h3>
      <p>
        We use first-party website analytics to understand how SantaSecret is used and to improve
        the website and service. Where the GDPR applies, this processing is based on our legitimate
        interest in measuring and improving the service, subject to the rights and interests of
        visitors. Details are in section 6.
      </p>

      <h3>3.6 Legal requirements</h3>
      <p>
        We may process or retain information where necessary to comply with applicable law, respond
        to lawful requests, or establish, exercise, or defend legal claims.
      </p>

      <h2 id="list-4">4. Information Shared Within a Secret Santa Game</h2>
      <p>
        SantaSecret is designed to allow people participating in the same Secret Santa game to
        exchange information needed for the game. What each role in a game can see depends on how
        the game is configured:
      </p>

      <h3>4.1 The game organizer</h3>
      <p>
        The game organizer needs enough information to run the game, so their view of each
        participant intentionally shows some fields and hides or masks others:
      </p>
      <ul>
        <li>
          <strong>Always visible to the organizer:</strong> the name or nickname you use in the
          game, your avatar, whether you have opened your invitation, whether email or Telegram
          notifications are enabled for your card, and your gift-sent and gift-received status.
        </li>
        <li>
          <strong>Shown as filled or not filled, with only a short partial preview</strong>{' '}
          (typically the first two or three characters followed by asterisks): phone number,
          delivery address, postal code, and postal recipient. The organizer can see that you have
          filled these fields in, but not their full contents.
        </li>
        <li>
          <strong>Hidden by default; the organizer can reveal the full text on request:</strong>{' '}
          your gift wish. The organizer sees a &quot;not filled in&quot; badge if you have not
          written anything, or a &quot;Show&quot; button if you have; clicking &quot;Show&quot;
          displays the full wish text to that organizer so they can help coordinate the draw or
          answer questions about your wishlist.
        </li>
        <li>
          <strong>Never visible to the organizer:</strong> the anonymous chat between you and your
          assigned Secret Santa.
        </li>
      </ul>
      <p>
        The organizer can also exchange messages one-to-one with each participant through a
        dedicated organizer chat. Those messages are not anonymous &mdash; the organizer sees who
        wrote them and the participant sees they are talking to the organizer.
      </p>
      <p>
        Because the organizer administers the game, they have access to any card information through
        the SantaSecret administrative interfaces. If you do not want a particular organizer to see
        something, do not enter it in SantaSecret, or ask a different person to organize the game.
      </p>

      <h3>4.2 Your assigned Secret Santa</h3>
      <p>
        After the draw, your assigned Secret Santa can see your gift wish, delivery address, and
        other information you filled in for receiving a gift. You can also exchange messages with
        your assigned Secret Santa through an anonymous in-game chat that does not reveal their
        identity to you or your identity to them.
      </p>

      <h3>4.3 Other participants</h3>
      <p>
        Other participants in the same game can see your name or nickname, avatar, and whether you
        have sent or received a gift &mdash; unless the organizer has chosen to hide names. Other
        participants cannot see your gift wish, delivery address, phone number, or private messages.
      </p>

      <h3>4.4 What is not shared</h3>
      <p>
        The anonymous chat between you and your assigned Secret Santa is not visible to the
        organizer or to other participants.
      </p>

      <h2 id="list-5">5. Service Providers</h2>
      <p>
        We use a small number of third-party service providers to operate SantaSecret. They process
        personal information as necessary to provide their services to us.
      </p>
      <table className="prose__table">
        <thead>
          <tr>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Data shared</th>
            <th>Region</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Postmark</td>
            <td>
              Transactional email delivery (invitations, draw results, reminders, account and
              security messages, notifications)
            </td>
            <td>
              Recipient email address, name or nickname, magic-link URL, invitation and result
              content
            </td>
            <td>United States</td>
          </tr>
          <tr>
            <td>Cloudflare</td>
            <td>
              Domain name resolution and inbound-mail forwarding for support email sent to{' '}
              <mark>postman@santa-secret.com</mark>
            </td>
            <td>Support message headers and content</td>
            <td>Global network</td>
          </tr>
          <tr>
            <td>Google (Gmail)</td>
            <td>Destination inbox for support mail forwarded by Cloudflare</td>
            <td>Support message headers and content</td>
            <td>Global (Google)</td>
          </tr>
          <tr>
            <td>Amazon Web Services (S3)</td>
            <td>
              Storage of user-uploaded files, such as participant-card avatars and messenger
              attachments
            </td>
            <td>Uploaded image files</td>
            <td>Frankfurt, Germany (EU)</td>
          </tr>
        </tbody>
      </table>
      <p>
        We do not use open-tracking or link-tracking pixels in the transactional emails we send
        through Postmark.
      </p>
      <p>Sign-in through an external authentication provider is described in section 8.</p>
      <p>
        Basic first-party website analytics are provided by a SantaSecret-hosted instance of Umami
        Analytics, described in section 6.
      </p>
      <p>
        We do not sell or rent personal information. We disclose information to providers for the
        purposes described in this policy, including payment processing below.
      </p>

      <h3 id="payments">Premium payments through Stripe</h3>
      <p>
        On santa-secret.com, Premium checkout is handled by Stripe using Stripe Managed Payments. We
        send Stripe your checkout email address, payment and order references, the Premium product
        description, price and currency. Stripe collects payment details directly on its hosted
        checkout and may process billing, device and fraud-prevention information under its own
        privacy policy. SantaSecret does not collect or store your full card number or card security
        code.
      </p>
      <p>
        We store the checkout email, order and provider transaction identifiers, amount, currency,
        payment status and timestamps, linked to the purchasing profile, to confirm payment,
        activate Premium, provide support and handle payment records. This applies to both guest and
        permanent profiles. The receipt email does not automatically become your account email or
        make a guest profile permanent.
      </p>
      <p>
        We process these records to fulfil your purchase and meet applicable accounting and legal
        obligations. Payment records may be retained where necessary for those obligations or
        payment disputes even after account deletion. Stripe may process information internationally
        and use its own cookies and browser storage during checkout. See{' '}
        <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
          Stripe’s Privacy Policy
        </a>{' '}
        and{' '}
        <a href="https://stripe.com/legal/cookies-policy" target="_blank" rel="noreferrer">
          Cookie Policy
        </a>
        .
      </p>

      <h2 id="list-6">6. Analytics</h2>
      <p>
        For basic website measurement we use a SantaSecret-hosted instance of Umami Analytics at{' '}
        <mark>analytics.santa-secret.com</mark>. That subdomain is operated by us on our own
        infrastructure &mdash; analytics data does not leave SantaSecret and is not shared with the
        Umami project or any third-party analytics vendor. Umami processes:
      </p>
      <ul>
        <li>the pages you visit and page titles;</li>
        <li>the referring website or page, if any;</li>
        <li>your browser language and screen dimensions;</li>
        <li>your browser, operating system, and device type;</li>
        <li>approximate country or region;</li>
        <li>timestamps and interactions with website functionality.</li>
      </ul>
      <p>
        Umami derives a pseudonymous, day-scoped session identifier on our server from your IP
        address, user-agent, and the requested website. Your IP address is not stored as a separate
        identifier by Umami and is not shared with any third party.
      </p>
      <p>
        Umami does not use cookies or other browser storage, and is not used by SantaSecret to track
        visitors across other websites. We configure Umami so that sensitive URL parameters (such as
        access tokens and email addresses) are redacted before analytics data is sent, and we do not
        intentionally send participant names, email addresses, mailing addresses, gift information,
        or messages to Umami.
      </p>
      <p>
        Umami is loaded on all SantaSecret pages without prior consent. Where the GDPR applies, this
        limited processing is based on our legitimate interest in measuring, maintaining, and
        improving the service, subject to your right to object at any time using the contact details
        in section 16.
      </p>

      <h2 id="cookies">7. Cookies and Browser Storage</h2>
      <p>
        SantaSecret uses cookies, localStorage, and sessionStorage. We use browser storage for
        authentication, preferences, game drafts, checkout continuity, Premium reminders and
        first-party Premium attribution, as described below. We do not use advertising or cross-site
        tracking cookies.
      </p>
      <table className="prose__table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Purpose</th>
            <th>Category</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>jwt</td>
            <td>Keeps you authenticated while using SantaSecret</td>
            <td>Strictly necessary</td>
            <td>Sign-in session (up to 90 days)</td>
          </tr>
          <tr>
            <td>profile_wallet</td>
            <td>
              Remembers the linked profiles you have opened in this browser so you can switch
              between them
            </td>
            <td>Strictly necessary</td>
            <td>Up to 90 days</td>
          </tr>
          <tr>
            <td>notice_closed</td>
            <td>Remembers that you dismissed an active service notice</td>
            <td>Functional</td>
            <td>Until overwritten or cleared</td>
          </tr>
          <tr>
            <td>santa_organizer_view:{'{game}'}</td>
            <td>Remembers whether you are viewing a game as organizer or as participant</td>
            <td>Functional</td>
            <td>Until cleared</td>
          </tr>
          <tr>
            <td>santa_notification_access_token</td>
            <td>Provides secure, temporary access from a service notification</td>
            <td>Strictly necessary</td>
            <td>Browser-tab session</td>
          </tr>
          <tr>
            <td>santa_notification_access_reason</td>
            <td>Remembers the status of a temporary notification-access link</td>
            <td>Strictly necessary</td>
            <td>Browser-tab session</td>
          </tr>
          <tr>
            <td>santa_notification_access_email</td>
            <td>Displays the masked email associated with a temporary notification-access link</td>
            <td>Strictly necessary</td>
            <td>Browser-tab session</td>
          </tr>
          <tr>
            <td>santa_card_access_token:{'{card}'}</td>
            <td>Provides secure, temporary access to the relevant participant card</td>
            <td>Strictly necessary</td>
            <td>Browser-tab session</td>
          </tr>
          <tr>
            <td>santa_card_access_unavailable:{'{card}'}</td>
            <td>Remembers that temporary card access is unavailable</td>
            <td>Strictly necessary</td>
            <td>Browser-tab session</td>
          </tr>
          <tr>
            <td>qd_organizer_access:{'{game}'}</td>
            <td>Remembers Quick Draw organizer access for the current browser tab</td>
            <td>Strictly necessary</td>
            <td>Browser-tab session</td>
          </tr>
          <tr>
            <td>santa_theme, santa_season</td>
            <td>localStorage: remembers appearance preferences</td>
            <td>Functional</td>
            <td>Until overwritten or cleared</td>
          </tr>
          <tr>
            <td>ss:new-box:draft:*; ss:new-box:current</td>
            <td>
              localStorage: game-creation draft, including entered names and settings, profile ID
              and current step; sessionStorage: current draft reference for resuming after checkout
            </td>
            <td>Functional</td>
            <td>
              Draft accepted for 24 hours; removed on completion or until cleared. Current
              reference: tab session
            </td>
          </tr>
          <tr>
            <td>ss:premium:return</td>
            <td>
              sessionStorage: return URL, profile ID and timestamp to resume the page or game
              creation after checkout
            </td>
            <td>Functional</td>
            <td>Used for up to 24 hours within the tab session</td>
          </tr>
          <tr>
            <td>ss:deferred-theme:*</td>
            <td>
              localStorage: preferred Premium theme, profile ID and reminder state for the game
            </td>
            <td>Personalization / Premium promotion</td>
            <td>Used for up to 7 days; removed when handled or until cleared</td>
          </tr>
          <tr>
            <td>ss:premium:source; ss:premium:checkout-source</td>
            <td>
              sessionStorage: first and latest Premium feature, page category, button placement and
              timestamp to understand checkout journeys
            </td>
            <td>Analytics</td>
            <td>
              Source: 30 minutes of inactivity; checkout snapshot: up to 24 hours within the tab
              session
            </td>
          </tr>
          <tr>
            <td>santa_link_invited:*</td>
            <td>localStorage: participant-card identifiers whose invitation links you copied</td>
            <td>Functional</td>
            <td>Until updated or cleared</td>
          </tr>
          <tr>
            <td>participant_access:*</td>
            <td>
              sessionStorage: newly issued participant recovery codes and access links, retained for
              the confirmation screen
            </td>
            <td>Strictly necessary</td>
            <td>Until acknowledged and cleared, or the tab session ends</td>
          </tr>
        </tbody>
      </table>
      <p>
        Premium interaction events include the feature and button selected, page category, theme,
        quoted price and currency, device category, purchase outcome, and whether the visitor is
        using a guest profile, a permanent account, or is not signed in. The attribution entries
        above contain categories and timestamps, not email addresses, card details or recovery
        codes. This first-party attribution storage is separate from Umami itself; it does not track
        you across other websites.
      </p>
      <p>
        localStorage can survive closing the browser; sessionStorage belongs to a tab and may be
        copied into a new checkout tab. Time limits above describe when the application accepts an
        entry: expired data may remain until accessed, overwritten or cleared. You can remove
        cookies and browser storage in your browser settings. Doing so can erase drafts and
        guest-profile access. Before clearing data, save your recovery codes or link and verify your
        email. We store the time you confirm saving recovery codes only after you submit that
        confirmation.
      </p>
      <p>
        Blocking this storage may prevent authentication or other parts of SantaSecret from working
        correctly. You can delete cookies and site storage at any time using your browser settings;
        clearing strictly necessary storage may sign you out or reset functional preferences.
      </p>

      <h2 id="list-8">8. External Authentication</h2>
      <p>
        You may choose to sign in to SantaSecret using an external authentication provider &mdash;
        currently <strong>Google</strong> or <strong>Telegram</strong>. If you do, that provider
        processes information and may use its own cookies and browser storage as part of the
        authentication flow. Those technologies are controlled by the relevant provider and are
        subject to that provider&apos;s privacy practices.
      </p>
      <p>
        When you use external authentication, we receive only the information necessary to complete
        the authentication process and provide your SantaSecret account (typically an email address
        and a display name).
      </p>

      <h2 id="list-9">9. International Processing and Transfers</h2>
      <p>
        SantaSecret&apos;s application servers are located in the{' '}
        <mark>Netherlands (European Union)</mark>. Some of the service providers listed in section 5
        process personal information in other countries, including the United States.
      </p>
      <p>
        Where applicable law (for example the GDPR) requires safeguards for international transfers
        of personal information, we and our service providers rely on appropriate legal transfer
        mechanisms &mdash; such as the EU&ndash;US Data Privacy Framework where the recipient is
        certified under it, or Standard Contractual Clauses.
      </p>

      <h2 id="list-10">10. Data Retention</h2>
      <p>
        We retain personal information only for as long as reasonably necessary for the purposes
        described in this Policy.
      </p>
      <ul>
        <li>
          <strong>Account information</strong> is retained until you delete your account.
        </li>
        <li>
          <strong>Secret Santa games</strong> and their participant cards are retained until the
          organizer deletes the game. Archiving a game does not delete its data.
        </li>
        <li>
          <strong>In-game chat messages</strong> are retained while the game exists. Resetting the
          draw hides earlier chat messages inside the app.
        </li>
        <li>
          <strong>Technical and security information</strong> may be retained for a limited period
          where necessary for security, troubleshooting, and abuse prevention.
        </li>
        <li>
          <strong>Support correspondence</strong> is retained where necessary to resolve requests.
        </li>
        <li>
          <strong>Analytics information</strong> collected through our SantaSecret-hosted Umami
          instance is retained as long as reasonably necessary to measure service usage and
          operational trends.
        </li>
        <li>
          <strong>Aggregated reports</strong> may remain available for longer periods where they no
          longer identify an individual user.
        </li>
      </ul>
      <p>
        <strong>When you delete your account</strong>, we replace your identifiers with a masked
        placeholder (email is set to a non-routable <code>user_deleted_&lt;id&gt;@deleted.com</code>{' '}
        value and your name is set to &quot;Deleted User&quot;), we overwrite your card&apos;s gift
        wish, phone number, and postal address with asterisks, and we update notification records
        referencing your email address so that your email is removed.
      </p>
      <p>
        Database backups are rotated on a rolling window of approximately <strong>3 days</strong>.
        Earlier information may remain in the most recent backups until they are overwritten within
        that window.
      </p>
      <p>
        We may retain certain information for longer where required by law or reasonably necessary
        to establish, exercise, or defend legal claims.
      </p>

      <h2 id="list-11">11. Your Privacy Rights</h2>
      <p>
        Depending on where you live and the law that applies to you, you may have rights regarding
        your personal information, including the right to:
      </p>
      <ul>
        <li>request access to personal information we hold about you;</li>
        <li>receive a copy of certain personal information;</li>
        <li>request correction of inaccurate or incomplete information;</li>
        <li>request deletion of your personal information;</li>
        <li>request restriction of certain processing;</li>
        <li>object to processing based on legitimate interests;</li>
        <li>receive certain information in a portable format;</li>
        <li>withdraw consent where processing is based on consent;</li>
        <li>lodge a complaint with an appropriate data-protection authority.</li>
      </ul>
      <p>
        To exercise a privacy right, contact us at{' '}
        <Link variant="inline" href="mailto:postman@santa-secret.com">
          postman@santa-secret.com
        </Link>
        . We may need to verify your identity before completing a request. Where the GDPR applies,
        we will respond without undue delay and generally within one month; that period may be
        extended for particularly complex or numerous requests where permitted by law.
      </p>
      <p>
        Residents of the European Economic Area may lodge a complaint with the data-protection
        authority in their country of residence. A list of authorities is maintained by the European
        Data Protection Board at{' '}
        <Link
          variant="inline"
          href="https://www.edpb.europa.eu/about-edpb/about-edpb/members_en"
          target="_blank"
        >
          edpb.europa.eu
        </Link>
        . Residents of the United Kingdom may lodge a complaint with the Information
        Commissioner&apos;s Office at{' '}
        <Link variant="inline" href="https://ico.org.uk/" target="_blank">
          ico.org.uk
        </Link>
        .
      </p>

      <h2 id="list-12">12. Children Under 13</h2>
      <p>
        SantaSecret is a general-audience service intended only for people who are at least 13 years
        old. If you are under 13, do not use SantaSecret, create an account, participate in a game
        through the service, or submit any personal information to us. In parts of the European
        Economic Area where the age of digital consent is higher than 13, you must be at least that
        age to use SantaSecret.
      </p>
      <p>
        An adult teacher, parent, legal guardian, or organizer may use SantaSecret to manage a game
        involving children under 13 only if all of the following apply:
      </p>
      <ul>
        <li>the adult controls all accounts and contact information used for the game;</li>
        <li>the children are not given direct access to SantaSecret;</li>
        <li>the adult uses non-identifying names or aliases for the children where practical;</li>
        <li>
          the adult does not submit email addresses, phone numbers, or postal addresses relating to
          the children;
        </li>
        <li>
          the adult has permission or another lawful authority to provide any limited participant
          information used;
        </li>
        <li>the adult shares the draw results with the children outside SantaSecret.</li>
      </ul>
      <p>
        We do not knowingly collect personal information directly from children under 13. If we
        learn that a child under 13 has accessed or used SantaSecret directly, or that a
        child&apos;s personal information has been submitted in circumstances where it should not
        have been collected, we will take reasonable steps to delete that information and disable
        the associated account or access.
      </p>
      <p>
        If you believe that a child&apos;s personal information has been provided to us in these
        circumstances, contact us at{' '}
        <Link variant="inline" href="mailto:postman@santa-secret.com">
          postman@santa-secret.com
        </Link>{' '}
        so that we can review and, where appropriate, delete it.
      </p>

      <h2 id="list-13">13. Security</h2>
      <p>
        We use reasonable technical and organizational measures designed to protect personal
        information, including:
      </p>
      <ul>
        <li>HTTPS for all connections to SantaSecret;</li>
        <li>Argon2id hashing for user passwords;</li>
        <li>signed session tokens (JSON Web Tokens) for authentication;</li>
        <li>restricted access to production systems and personal information.</li>
      </ul>
      <p>
        No method of transmission or storage is completely secure, and we cannot guarantee absolute
        security.
      </p>

      <h2 id="list-14">14. Automated Decision-Making</h2>
      <p>
        SantaSecret does not make decisions about you using solely automated processing that produce
        legal or similarly significant effects. Secret Santa drawings assign each participant a
        giftee at random within the rules configured by the game organizer; the drawing is a
        matching procedure and is not an automated decision within the meaning of Article 22 of the
        GDPR.
      </p>

      <h2 id="list-15">15. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy when our service, service providers, technologies, or
        legal obligations change. The current version will be available through the Privacy Policy
        link on <mark>santa-secret.com</mark>. If a change materially affects how we process
        personal information, we will provide additional notice where required by applicable law.
      </p>

      <h2 id="list-16">16. Contact Us</h2>
      <p>
        For questions about this Policy, privacy requests, or concerns about how your personal
        information is processed, contact:
      </p>
      <p>
        <strong>Santa Secret LLC</strong>
        <br />
        2108 N ST STE N
        <br />
        Sacramento, CA 95816
        <br />
        United States
        <br />
        <strong>Email:</strong>{' '}
        <Link variant="inline" href="mailto:postman@santa-secret.com">
          postman@santa-secret.com
        </Link>
      </p>
    </Prose>
  );
}
